import { useEffect, useState, useCallback } from 'react'
import { Box, Button, Checkbox, FormControl, FormErrorMessage, FormLabel, Grid, HStack, Input, VStack } from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type ParentTicketStatusFormProps = { statusId?: number; onSuccess?: () => void; onCancel?: () => void }

const initialValues = { code: '', label: '', is_closed: false, is_on_hold: false }

export function ParentTicketStatusForm({ statusId, onSuccess, onCancel }: ParentTicketStatusFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!statusId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)

  const loadOne = useCallback(async () => {
    if (!statusId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/parent-ticket-statuses/${statusId}`)
      const r = data?.data ?? data
      setForm({ code: r.code ?? '', label: r.label ?? '', is_closed: r.is_closed === true, is_on_hold: r.is_on_hold === true })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [statusId, showError])

  useEffect(() => {
    if (statusId) loadOne()
    else setForm(initialValues)
  }, [statusId, loadOne])

  const updateField = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => { const next = { ...prev }; delete next[field]; return next })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = { code: form.code.trim(), label: form.label.trim() || null, is_closed: form.is_closed, is_on_hold: form.is_on_hold }
      if (statusId) {
        await apiClient.put(`/service-catalog/parent-ticket-statuses/${statusId}`, payload)
        showSuccess('Success', 'Ticket status updated successfully')
      } else {
        await apiClient.post('/service-catalog/parent-ticket-statuses', payload)
        showSuccess('Success', 'Ticket status created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Code *</FormLabel>
          <FormControl isInvalid={!!errors.code?.length} isRequired>
            <Input value={form.code} onChange={(e) => updateField('code', e.target.value)} placeholder="e.g. OPEN" isDisabled={!!statusId} />
            {!!statusId && <Box as="span" fontSize="sm" color="gray.500">Code cannot be changed after creation.</Box>}
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Label</FormLabel>
          <FormControl isInvalid={!!errors.label?.length}>
            <Input value={form.label} onChange={(e) => updateField('label', e.target.value)} placeholder="e.g. Open" />
            <FormErrorMessage>{errors.label?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Closed</FormLabel>
          <FormControl>
            <Checkbox isChecked={form.is_closed} onChange={(e) => updateField('is_closed', e.target.checked)} colorScheme="blue">This status is a closed state.</Checkbox>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>On hold</FormLabel>
          <FormControl>
            <Checkbox isChecked={form.is_on_hold} onChange={(e) => updateField('is_on_hold', e.target.checked)} colorScheme="blue">This status puts the ticket on hold.</Checkbox>
          </FormControl>
        </Grid>
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && <Button variant="outline" onClick={onCancel} isDisabled={saving}>Cancel</Button>}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={statusId ? 'Saving...' : 'Creating...'}>{statusId ? 'Save' : 'Create Ticket Status'}</Button>
        </HStack>
      </VStack>
    </Box>
  )
}
